import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  IconButton,
  Paper,
  Stack
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StatusChip = styled(Chip)(({ status }) => ({
  fontWeight: 600,
  ...(status === 'open' && {
    backgroundColor: '#4caf50',
    color: 'white',
  }),
  ...(status === 'closing_soon' && {
    backgroundColor: '#ff9800',
    color: 'white',
  }),
  ...(status === 'closed' && {
    backgroundColor: '#f44336',
    color: 'white',
  }),
  ...(status === 'upcoming' && {
    backgroundColor: '#2196f3',
    color: 'white',
  }),
}));

const MetricCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  border: '1px solid rgba(255,255,255,0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
  }
}));

const HorizonEuropeIntegration = () => {
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLiveData();
    // Set up periodic updates every 30 minutes
    const interval = setInterval(fetchLiveData, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchLiveData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Simulate API call to official Horizon Europe data sources
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock real-time data structure
      const mockData = {
        overview: {
          total_budget: 95500000000, // €95.5 billion
          allocated_budget: 23800000000, // €23.8 billion
          active_calls: 247,
          closing_soon: 18,
          applications_received: 12450,
          success_rate: 12.8
        },
        recent_calls: [
          {
            id: 'HORIZON-HLTH-2024-CARE-08-02',
            title: 'Personalised Medicine approaches for depression and anxiety disorders',
            program: 'Health',
            cluster: 'Health',
            budget: 15000000,
            deadline: '2024-04-17T17:00:00Z',
            status: 'open',
            applications_received: 45,
            expected_applications: 120,
            last_updated: '2024-01-15T14:30:00Z'
          },
          {
            id: 'HORIZON-CL4-2024-DIGITAL-EMERGING-01-07',
            title: 'Advanced quantum computing systems',
            program: 'Digital, Industry and Space',
            cluster: 'Digital, Industry and Space',
            budget: 25000000,
            deadline: '2024-03-21T17:00:00Z',
            status: 'closing_soon',
            applications_received: 78,
            expected_applications: 90,
            last_updated: '2024-01-15T15:45:00Z'
          },
          {
            id: 'HORIZON-CL5-2024-D3-01-05',
            title: 'Sustainable and smart cities and communities',
            program: 'Climate, Energy and Mobility',
            cluster: 'Climate, Energy and Mobility',
            budget: 30000000,
            deadline: '2024-05-22T17:00:00Z',
            status: 'upcoming',
            applications_received: 0,
            expected_applications: 200,
            last_updated: '2024-01-15T16:20:00Z'
          }
        ],
        trending_topics: [
          { topic: 'Artificial Intelligence', calls: 34, budget: 156000000 },
          { topic: 'Climate Change', calls: 28, budget: 234000000 },
          { topic: 'Digital Health', calls: 19, budget: 89000000 },
          { topic: 'Quantum Computing', calls: 12, budget: 178000000 },
          { topic: 'Green Energy', calls: 31, budget: 345000000 }
        ],
        deadlines_this_week: [
          { id: 1, title: 'AI for Healthcare', deadline: '2024-01-18', budget: 12000000 },
          { id: 2, title: 'Quantum Sensors', deadline: '2024-01-19', budget: 8500000 },
          { id: 3, title: 'Smart Cities IoT', deadline: '2024-01-20', budget: 15000000 }
        ],
        system_status: {
          api_status: 'operational',
          last_sync: '2024-01-15T16:30:00Z',
          data_freshness: 'real-time',
          response_time: 234
        }
      };
      
      setLiveData(mockData);
      setLastUpdate(new Date());
    } catch (err) {
      setError('Failed to fetch live data from Horizon Europe API');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000000) return `€${(amount / 1000000000).toFixed(1)}B`;
    if (amount >= 1000000) return `€${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `€${(amount / 1000).toFixed(0)}K`;
    return `€${amount}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-EU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTimeUntilDeadline = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffInDays = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    
    if (diffInDays < 0) return 'Closed';
    if (diffInDays === 0) return 'Today!';
    if (diffInDays === 1) return 'Tomorrow';
    if (diffInDays <= 7) return `${diffInDays} days`;
    return formatDate(deadline);
  };

  if (loading && !liveData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Fetching Live Horizon Europe Data...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Connecting to official EU funding databases
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" onClick={fetchLiveData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Horizon Europe Live Data
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Real-time funding information from official EU sources
          </Typography>
        </Box>
        
        <Box display="flex" alignItems="center" gap={2}>
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastUpdate?.toLocaleTimeString()}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box 
                sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: 'success.main',
                  animation: 'pulse 2s infinite'
                }} 
              />
              <Typography variant="caption" color="success.main">
                Live
              </Typography>
            </Box>
          </Box>
          
          <IconButton 
            onClick={fetchLiveData} 
            disabled={loading}
            aria-label="Refresh data"
            sx={{ 
              animation: loading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* System Status */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <CheckCircleIcon color="success" />
          <Typography variant="body2">
            System Status: {liveData.system_status.api_status} • 
            Response Time: {liveData.system_status.response_time}ms • 
            Data Freshness: {liveData.system_status.data_freshness}
          </Typography>
        </Box>
      </Paper>

      {/* Overview Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {formatCurrency(liveData.overview.total_budget)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Budget
              </Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                {formatCurrency(liveData.overview.allocated_budget)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Allocated
              </Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                {liveData.overview.active_calls}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Calls
              </Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>
                {liveData.overview.closing_soon}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Closing Soon
              </Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'info.main' }}>
                {liveData.overview.applications_received.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Applications
              </Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                {liveData.overview.success_rate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Success Rate
              </Typography>
            </CardContent>
          </MetricCard>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        {/* Recent Calls */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Latest Call Updates
              </Typography>
              
              {liveData.recent_calls.map((call) => (
                <Card key={call.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {call.title}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Chip label={call.id} size="small" variant="outlined" />
                          <Chip label={call.cluster} size="small" color="primary" />
                          <StatusChip label={call.status.replace('_', ' ')} status={call.status} size="small" />
                        </Box>
                      </Box>
                      
                      <Box textAlign="right">
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {formatCurrency(call.budget)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Budget
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Deadline: {getTimeUntilDeadline(call.deadline)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Applications: {call.applications_received}/{call.expected_applications}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={(call.applications_received / call.expected_applications) * 100}
                          sx={{ width: 100, mb: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {Math.round((call.applications_received / call.expected_applications) * 100)}% filled
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar Content */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Trending Topics */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Trending Research Areas
                </Typography>
                <List dense>
                  {liveData.trending_topics.map((topic, index) => (
                    <ListItem key={index} divider>
                      <ListItemIcon>
                        <TrendingUpIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={topic.topic}
                        secondary={`${topic.calls} calls • ${formatCurrency(topic.budget)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* Urgent Deadlines */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Deadlines This Week
                </Typography>
                <List dense>
                  {liveData.deadlines_this_week.map((deadline) => (
                    <ListItem key={deadline.id} divider>
                      <ListItemIcon>
                        <WarningIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary={deadline.title}
                        secondary={`${formatDate(deadline.deadline)} • ${formatCurrency(deadline.budget)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default HorizonEuropeIntegration;